import { FC } from 'react';
import { RouteComponentProps } from 'react-router';

import { useTabs } from 'contexts/TabContext';
import { isTabHostSection, TabRecordType, TabSection } from 'constants/tabs';
import { RECORD_PAGE, SECTION_PAGE } from './pageRegistry';

/**
 * Builds the component a section's route renders.
 *
 * It stays inside the existing ConditionalRoute, so the module permission check remains exactly
 * where it has always been, and it serves both `/section` and `/section/:id` from a single route —
 * two routes would unmount and remount TabHost on every move between list and record, and every
 * open tab would refetch.
 *
 * With the feature switched off it renders the same two pages the routes rendered before.
 * Schedule has no record type of its own, so it only ever renders its list page.
 */
const buildSectionRoute = (section: TabSection, recordType?: TabRecordType): FC<RouteComponentProps<{ id?: string }>> => {
  const SectionRoute: FC<RouteComponentProps<{ id?: string }>> = props => {
    const { match } = props;
    const { isTabNavActive } = useTabs();

    /**
     * TabHost is mounted once by App, not here.
     *
     * Tabs can hold records from any module, so the matching route changes as the user switches
     * tabs — a quotation tab matches /quotations/:id, a job tab matches /jobs/:id. Rendering TabHost
     * from inside the route would unmount and remount it on every switch, and every open tab would
     * refetch. The route stays for what it is genuinely good at: the permission check.
     */
    if (isTabNavActive && isTabHostSection(section)) {
      return null;
    }

    const ListPage = SECTION_PAGE[section];
    const DetailPage = recordType && RECORD_PAGE[recordType];

    if (match.params.id && DetailPage) {
      return <DetailPage recordId={match.params.id} />;
    }

    return ListPage ? <ListPage /> : null;
  };

  SectionRoute.displayName = `${section}SectionRoute`;
  return SectionRoute;
};

export const QuotationsSection = buildSectionRoute(TabSection.QUOTATIONS, TabRecordType.QUOTATION);
export const JobsSection = buildSectionRoute(TabSection.JOBS, TabRecordType.JOB);
export const ClientsSection = buildSectionRoute(TabSection.CLIENTS, TabRecordType.CLIENT);
export const InvoicesSection = buildSectionRoute(TabSection.INVOICES, TabRecordType.INVOICE);
export const ScheduleSection = buildSectionRoute(TabSection.SCHEDULE);
